import React, { useEffect, useState } from 'react';
import { api } from '../api';
import { todayISO } from '../utils/format';

const STARS = [1, 2, 3, 4, 5];
const GESTATION_DAYS = 147;

const emptyLambingForm = () => ({
  lambing_date: todayISO(),
  lambs_born: '',
  lambs_weaned: '',
  mothering_score: '',
  notes: '',
});

const emptyBreedingForm = () => ({
  ram_tag: '',
  mating_date: todayISO(),
  method: 'natural',
  expected_lambing_date: '',
  status: 'pending',
  notes: '',
});

const addDays = (iso, days) => {
  if (!iso) return '';
  const d = new Date(iso);
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
};

const BREEDING_STATUS_LABELS = {
  pending: 'Pending',
  confirmed: 'Confirmed',
  lambed: 'Lambed',
  failed: 'Failed',
};

export default function ReproductionTab({ sheepId, sex }) {
  const [records, setRecords] = useState([]);
  const [breeding, setBreeding] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyLambingForm);
  const [showBreedForm, setShowBreedForm] = useState(false);
  const [editingBreed, setEditingBreed] = useState(null);
  const [breedForm, setBreedForm] = useState(emptyBreedingForm);
  const [error, setError] = useState('');

  const load = () => {
    setError('');
    Promise.all([api.listLambing(sheepId), api.listBreeding(sheepId)])
      .then(([lambing, breed]) => { setRecords(lambing); setBreeding(breed); })
      .catch(err => setError(err.message));
  };

  useEffect(() => { if (sex === 'ewe') load(); }, [sheepId, sex]);

  if (sex !== 'ewe') {
    return <p className="text-muted">Reproduction records are only tracked for ewes.</p>;
  }

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const setBreed = (k, v) => setBreedForm(f => {
    const next = { ...f, [k]: v };
    // Auto-fill expected lambing when the mating date changes and no manual override.
    if (k === 'mating_date' && (!f.expected_lambing_date || f.expected_lambing_date === addDays(f.mating_date, GESTATION_DAYS))) {
      next.expected_lambing_date = addDays(v, GESTATION_DAYS);
    }
    return next;
  });

  // ---- Lambing ----
  const startAdd = () => { setEditing(null); setForm(emptyLambingForm()); setShowForm(v => !v); };
  const startEdit = (record) => {
    setEditing(record);
    setForm({
      lambing_date: record.lambing_date,
      lambs_born: record.lambs_born,
      lambs_weaned: record.lambs_weaned ?? '',
      mothering_score: record.mothering_score ?? '',
      notes: record.notes || '',
    });
    setShowForm(true);
  };
  const closeForm = () => { setShowForm(false); setEditing(null); setForm(emptyLambingForm()); };

  const handleSubmit = async () => {
    if (form.lambs_born === '') { setError('Lambs born is required.'); return; }
    try {
      const payload = {
        ...form,
        lambs_born: parseInt(form.lambs_born, 10),
        lambs_weaned: form.lambs_weaned !== '' ? parseInt(form.lambs_weaned, 10) : null,
        mothering_score: form.mothering_score !== '' ? parseInt(form.mothering_score, 10) : null,
      };
      editing ? await api.updateLambing(editing.id, payload) : await api.addLambing(sheepId, payload);
      closeForm();
      load();
    } catch (err) { setError(err.message); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this lambing record?')) return;
    try { await api.deleteLambing(id); load(); } catch (err) { setError(err.message); }
  };

  // ---- Breeding ----
  const startAddBreed = () => { setEditingBreed(null); setBreedForm(emptyBreedingForm()); setShowBreedForm(v => !v); };
  const startEditBreed = (record) => {
    setEditingBreed(record);
    setBreedForm({
      ram_tag: record.ram_tag || '',
      mating_date: record.mating_date,
      method: record.method || 'natural',
      expected_lambing_date: record.expected_lambing_date || '',
      status: record.status || 'pending',
      notes: record.notes || '',
    });
    setShowBreedForm(true);
  };
  const closeBreedForm = () => { setShowBreedForm(false); setEditingBreed(null); setBreedForm(emptyBreedingForm()); };

  const submitBreed = async () => {
    if (!breedForm.mating_date) { setError('Mating date is required.'); return; }
    try {
      const payload = {
        ...breedForm,
        expected_lambing_date: breedForm.expected_lambing_date || null,
        ram_tag: breedForm.ram_tag || null,
      };
      editingBreed ? await api.updateBreeding(editingBreed.id, payload) : await api.addBreeding(sheepId, payload);
      closeBreedForm();
      load();
    } catch (err) { setError(err.message); }
  };

  const deleteBreed = async (id) => {
    if (!window.confirm('Delete this breeding record?')) return;
    try { await api.deleteBreeding(id); load(); } catch (err) { setError(err.message); }
  };

  const totalBorn = records.reduce((s, r) => s + r.lambs_born, 0);
  const totalWeaned = records.reduce((s, r) => s + (r.lambs_weaned || 0), 0);
  const today = todayISO();

  return (
    <div>
      {error && <div className="alert alert-danger mb-4">{error}</div>}

      {/* Breeding */}
      <div className="section-header">
        <span className="section-title">💞 Breeding</span>
        <button className="btn btn-secondary btn-sm" onClick={startAddBreed}>+ Add</button>
      </div>

      {showBreedForm && (
        <div className="inline-form mt-3 mb-4">
          <div className="form-grid form-grid-2">
            <div className="field">
              <label>Mating Date *</label>
              <input type="date" value={breedForm.mating_date} onChange={e => setBreed('mating_date', e.target.value)} />
            </div>
            <div className="field">
              <label>Ram Tag</label>
              <input value={breedForm.ram_tag} onChange={e => setBreed('ram_tag', e.target.value)} placeholder="e.g. RAM-07" />
            </div>
            <div className="field">
              <label>Method</label>
              <select value={breedForm.method} onChange={e => setBreed('method', e.target.value)}>
                <option value="natural">Natural</option>
                <option value="ai">AI</option>
              </select>
            </div>
            <div className="field">
              <label>Expected Lambing</label>
              <input type="date" value={breedForm.expected_lambing_date} onChange={e => setBreed('expected_lambing_date', e.target.value)} />
            </div>
            <div className="field">
              <label>Status</label>
              <select value={breedForm.status} onChange={e => setBreed('status', e.target.value)}>
                {Object.entries(BREEDING_STATUS_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
            <div className="field" style={{ gridColumn: '1 / -1' }}>
              <label>Notes</label>
              <textarea value={breedForm.notes} onChange={e => setBreed('notes', e.target.value)} placeholder="Any notes about this mating..." />
            </div>
          </div>
          <div className="form-actions mt-3">
            <button className="btn btn-secondary btn-sm" onClick={closeBreedForm}>Cancel</button>
            <button className="btn btn-primary btn-sm" onClick={submitBreed}>{editingBreed ? 'Save Changes' : 'Save'}</button>
          </div>
        </div>
      )}

      {breeding.length === 0 && !showBreedForm ? (
        <p className="text-muted mt-2">No breeding records yet.</p>
      ) : breeding.length > 0 && (
        <div className="table-wrap mt-2">
          <table>
            <thead><tr><th>Mating Date</th><th>Ram</th><th>Method</th><th>Expected Lambing</th><th>Status</th><th></th></tr></thead>
            <tbody>
              {breeding.map(b => {
                const dueSoon = b.expected_lambing_date && ['pending', 'confirmed'].includes(b.status);
                const overdue = dueSoon && b.expected_lambing_date < today;
                return (
                  <tr key={b.id}>
                    <td data-label="Mating Date">{b.mating_date}</td>
                    <td data-label="Ram">{b.ram_tag || '—'}</td>
                    <td data-label="Method" style={{ textTransform: 'uppercase' }}>{b.method || '—'}</td>
                    <td data-label="Expected Lambing" style={overdue ? { color: 'var(--rust)', fontWeight: 600 } : {}}>
                      {b.expected_lambing_date || '—'}{overdue ? ' ⚠' : ''}
                    </td>
                    <td data-label="Status"><span className={`badge badge-breeding-${b.status}`}>{BREEDING_STATUS_LABELS[b.status] || b.status}</span></td>
                    <td data-label="Actions">
                      <div className="row-actions">
                        <button className="btn btn-secondary btn-sm" onClick={() => startEditBreed(b)}>Edit</button>
                        <button className="btn btn-danger btn-sm btn-icon" onClick={() => deleteBreed(b.id)}>✕</button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <div className="divider" />

      {/* Lambing */}
      <div className="section-header">
        <span className="section-title">🐣 Lambing Records</span>
        <button className="btn btn-secondary btn-sm" onClick={startAdd}>+ Add</button>
      </div>

      {records.length > 0 && (
        <div className="summary-stats mb-4">
          <div className="stat-card" style={{ flex: '1', minWidth: 120 }}>
            <div className="stat-value">{records.length}</div>
            <div className="stat-label">Lambings</div>
          </div>
          <div className="stat-card" style={{ flex: '1', minWidth: 120 }}>
            <div className="stat-value">{totalBorn}</div>
            <div className="stat-label">Total Born</div>
          </div>
          <div className="stat-card" style={{ flex: '1', minWidth: 120 }}>
            <div className="stat-value">{totalWeaned}</div>
            <div className="stat-label">Total Weaned</div>
          </div>
        </div>
      )}

      {showForm && (
        <div className="inline-form mt-3 mb-4">
          <div className="form-grid form-grid-2">
            <div className="field">
              <label>Lambing Date</label>
              <input type="date" value={form.lambing_date} onChange={e => set('lambing_date', e.target.value)} />
            </div>
            <div className="field">
              <label>Lambs Born *</label>
              <input type="number" min="0" value={form.lambs_born} onChange={e => set('lambs_born', e.target.value)} placeholder="e.g. 2" />
            </div>
            <div className="field">
              <label>Lambs Weaned</label>
              <input type="number" min="0" value={form.lambs_weaned} onChange={e => set('lambs_weaned', e.target.value)} />
            </div>
            <div className="field">
              <label>Mothering Score (1-5)</label>
              <div className="flex gap-2 items-center" style={{ paddingTop: 4 }}>
                {STARS.map(n => (
                  <button
                    type="button"
                    className="star-button"
                    key={n}
                    onClick={() => set('mothering_score', form.mothering_score === String(n) || form.mothering_score === n ? '' : String(n))}
                    style={{ opacity: Number(form.mothering_score) >= n ? 1 : 0.3 }}
                  >★</button>
                ))}
              </div>
            </div>
            <div className="field" style={{ gridColumn: '1 / -1' }}>
              <label>Notes</label>
              <textarea value={form.notes} onChange={e => set('notes', e.target.value)} placeholder="Any notes about this lambing..." />
            </div>
          </div>
          <div className="form-actions mt-3">
            <button className="btn btn-secondary btn-sm" onClick={closeForm}>Cancel</button>
            <button className="btn btn-primary btn-sm" onClick={handleSubmit}>{editing ? 'Save Changes' : 'Save'}</button>
          </div>
        </div>
      )}

      {records.length === 0 && !showForm ? (
        <p className="text-muted mt-2">No lambing records yet.</p>
      ) : records.length > 0 && (
        <div className="table-wrap mt-2">
          <table>
            <thead><tr><th>Date</th><th>Born</th><th>Weaned</th><th>Mothering</th><th>Notes</th><th></th></tr></thead>
            <tbody>
              {records.map(r => (
                <tr key={r.id}>
                  <td data-label="Date">{r.lambing_date}</td>
                  <td data-label="Born">{r.lambs_born}</td>
                  <td data-label="Weaned">{r.lambs_weaned ?? '—'}</td>
                  <td data-label="Mothering">
                    {r.mothering_score
                      ? <span title={`${r.mothering_score}/5`}>{'★'.repeat(r.mothering_score)}{'☆'.repeat(5 - r.mothering_score)}</span>
                      : '—'}
                  </td>
                  <td data-label="Notes" className="text-muted">{r.notes || '—'}</td>
                  <td data-label="Actions">
                    <div className="row-actions">
                      <button className="btn btn-secondary btn-sm" onClick={() => startEdit(r)}>Edit</button>
                      <button className="btn btn-danger btn-sm btn-icon" onClick={() => handleDelete(r.id)}>✕</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
